import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@rentspree/component-2023.components.atoms.button';
import { Chip } from '@rentspree/component-2023.components.atoms.chip';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

import {
  TabNavigation,
  type Tab,
  TopNavigation,
  PropertyDetailOverviewTab,
  type ChecklistStep,
  RecentActivity,
  ApplicationsTab,
} from '../components/organisms';
import { useListingActivity } from '../hooks/useRecentActivity';

// Constants
const PROPERTY_DATA = {
  title: 'Suburban Family Home',
  address: '1234 Oak Street, San Francisco, CA 94102',
  image:
    'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80',
  status: 'Draft',
};

const USER_DATA = {
  avatarSrc: 'https://mui.com/static/images/avatar/1.jpg',
  avatarAlt: 'John Doe',
};

const TABS: Tab[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'applications', label: 'Applications' },
  { key: 'lease', label: 'Lease' },
  { key: 'payments', label: 'Payments' },
  { key: 'maintenance', label: 'Maintenance' },
];

const ListingDetailPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const navigate = useNavigate();
  const { id: listingId } = useParams<{ id: string }>();

  // Use repository to fetch recent activities for this listing
  const { activities, loading, error } = useListingActivity(listingId || '', 5);

  // Redirect to listings page if no listingId is provided
  React.useEffect(() => {
    if (!listingId) {
      navigate('/');
    }
  }, [listingId, navigate]);

  if (!listingId) {
    return null;
  }

  const handleMenuItemClick = (_item: string) => {
    // Handle navigation between menu items
    // TODO: Implement actual navigation logic
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const handlePublishListing = () => {
    // TODO: Implement publish listing logic
  };

  const handleStartScreening = () => {
    navigate('/add-tenant-screening');
  };

  const handleFindAgent = () => {
    // TODO: Implement find agent logic
  };

  const checklistSteps: ChecklistStep[] = [
    {
      number: 1,
      title: 'Market Listing',
      subtitle: 'Ready to publish your listing?',
      description:
        'Complete the final details to get your property on the market and start accepting applications.',
      isActive: true,
      isCompleted: false,
      buttonText: 'Publish Listing',
      onButtonClick: handlePublishListing,
    },
    {
      number: 2,
      title: 'Screen Applicants',
      description: 'Publish your listing to unlock',
      isActive: false,
      isCompleted: false,
    },
    {
      number: 3,
      title: 'Set up lease',
      description: 'Publish your listing to unlock',
      isActive: false,
      isCompleted: false,
    },
    {
      number: 4,
      title: 'Arrange payments',
      description: 'Publish your listing to unlock',
      isActive: false,
      isCompleted: false,
    },
  ];

  return (
    <Box
      sx={{
        background: '#ffffff',
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <TopNavigation
        activeItem='listings'
        onMenuItemClick={handleMenuItemClick}
        userAvatarSrc={USER_DATA.avatarSrc}
        userAvatarAlt={USER_DATA.avatarAlt}
      />

      {/* Property Header Section */}
      <Box
        sx={{
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          py: 3,
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '100%', md: 1280 },
            margin: '0 auto',
            px: { xs: 2, sm: 3, md: 6 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: { xs: 2, md: 3 },
            }}
          >
            <Box
              component='img'
              src={PROPERTY_DATA.image}
              alt='Property'
              sx={{
                width: { xs: 120, md: 160 },
                height: { xs: 120, md: 160 },
                objectFit: 'cover',
                borderRadius: 1.5,
              }}
            />
            <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant='h4'
                sx={{
                  fontWeight: 700,
                  color: '#222222',
                  mb: 1,
                  fontSize: { xs: '1.5rem', md: '2.125rem' },
                }}
              >
                {PROPERTY_DATA.title}
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: '#6b7280',
                  mb: 2,
                }}
              >
                {PROPERTY_DATA.address}
              </Typography>
              <Chip
                color='gray'
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#b5b5b5',
                        mr: 0.5,
                      }}
                    />
                    <Typography variant='caption' sx={{ fontWeight: 500 }}>
                      {PROPERTY_DATA.status}
                    </Typography>
                  </Box>
                }
                variant='filled'
                size='medium'
                sx={{
                  borderRadius: 9999,
                  mb: 2,
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <Button
                  variant='outlined'
                  color='secondary'
                  size='medium'
                  startIcon={<VisibilityIcon fontSize='small' />}
                >
                  View Public
                </Button>
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Button
                    variant='outlined'
                    color='primary'
                    size='medium'
                    startIcon={<EditIcon fontSize='small' />}
                  >
                    Edit Listing
                  </Button>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Button
                variant='outlined'
                color='primary'
                size='medium'
                startIcon={<EditIcon fontSize='small' />}
              >
                Edit Listing
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <TabNavigation
        tabs={TABS}
        activeTab={activeTab}
        onTabClick={handleTabClick}
      />

      {/* Main Content */}
      <Box
        sx={{
          background: '#ffffff',
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 10 },
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '100%', md: 1280 },
            margin: '0 auto',
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 3, md: 4 },
          }}
        >
          {activeTab === 'overview' && (
            <>
              <PropertyDetailOverviewTab
                steps={checklistSteps}
                style={{ flex: 1 }}
              />
              <RecentActivity
                activities={loading ? [] : activities}
                title={
                  loading
                    ? 'Loading...'
                    : error
                      ? 'Error loading activities'
                      : 'Recent Activity'
                }
              />
            </>
          )}
          {activeTab === 'applications' && (
            <ApplicationsTab
              listingId={listingId}
              onStartScreening={handleStartScreening}
              onFindAgent={handleFindAgent}
              style={{ width: '100%' }}
            />
          )}
          {activeTab === 'lease' && (
            <Box sx={{ flex: 1 }}>
              <Typography variant='h5' sx={{ color: '#222222', mb: 3 }}>
                Lease
              </Typography>
              <Typography variant='body1' sx={{ color: '#6b7280' }}>
                Lease management will be available after accepting an
                application.
              </Typography>
            </Box>
          )}
          {activeTab === 'payments' && (
            <Box sx={{ flex: 1 }}>
              <Typography variant='h5' sx={{ color: '#222222', mb: 3 }}>
                Payments
              </Typography>
              <Typography variant='body1' sx={{ color: '#6b7280' }}>
                Payment tracking will be available after setting up a lease.
              </Typography>
            </Box>
          )}
          {activeTab === 'maintenance' && (
            <Box sx={{ flex: 1 }}>
              <Typography variant='h5' sx={{ color: '#222222', mb: 3 }}>
                Maintenance
              </Typography>
              <Typography variant='body1' sx={{ color: '#6b7280' }}>
                Maintenance requests will be available after tenant move-in.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ListingDetailPage;
